"use client";
import { createButton } from "@gluestack-ui/core/button/creator";
import { PrimitiveIcon, UIIcon } from "@gluestack-ui/core/icon/creator";
import {
  tva,
  useStyleContext,
  withStyleContext,
  type VariantProps,
} from "@gluestack-ui/utils/nativewind-utils";
import { cssInterop } from "nativewind";
import React from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";

const SCOPE = "BUTTON";

const Root = withStyleContext(Pressable, SCOPE);

const UIButton = createButton({
  Root: Root,
  Text,
  Group: View,
  Spinner: ActivityIndicator,
  Icon: UIIcon,
});

cssInterop(PrimitiveIcon, {
  className: {
    target: "style",
    nativeStyleToProp: {
      height: true,
      width: true,
      fill: true,
      color: "classNameColor",
      stroke: true,
    },
  },
});

const buttonStyle = tva({
  base: "group/button rounded bg-primary-500 flex-row items-center justify-center data-[focus-visible=true]:web:outline-none data-[focus-visible=true]:web:ring-2 data-[disabled=true]:opacity-40 gap-2",
  variants: {
    action: {
      primary:
        "bg-primary-500 data-[hover=true]:bg-primary-600 data-[active=true]:bg-primary-700 border-primary-300 data-[hover=true]:border-primary-400 data-[active=true]:border-primary-500 data-[focus-visible=true]:web:ring-indicator-info",
      secondary:
        "bg-secondary-500 border-secondary-300 data-[hover=true]:bg-secondary-600 data-[hover=true]:border-secondary-400 data-[active=true]:bg-secondary-700 data-[active=true]:border-secondary-700 data-[focus-visible=true]:web:ring-indicator-info",
      positive:
        "bg-success-500 border-success-300 data-[hover=true]:bg-success-600 data-[hover=true]:border-success-400 data-[active=true]:bg-success-700 data-[active=true]:border-success-500 data-[focus-visible=true]:web:ring-indicator-info",
      negative:
        "bg-error-500 border-error-300 data-[hover=true]:bg-error-600 data-[hover=true]:border-error-400 data-[active=true]:bg-error-700 data-[active=true]:border-error-500 data-[focus-visible=true]:web:ring-indicator-info",
      default:
        "bg-transparent data-[hover=true]:bg-background-50 data-[active=true]:bg-transparent",
      facebook: "data-[active=true]:bg-blue-700 bg-blue-600",
      instagram:
        "data-[active=true]:bg-black data-[active=true]:opacity-5 bg-transparent",
      card: "bg-background-50 data-[active=true]:bg-background-300 data-[hover=true]:bg-background-200",
      elevated:
        "bg-background-0 data-[active=true]:bg-background-200 data-[hover=true]:bg-background-100",
      brand:
        "bg-brand-300 data-[active=true]:bg-brand-500 data-[hover=true]:bg-brand-400",
      brandLight:
        "bg-brand-100 border-brand-100 data-[hover=true]:bg-brand-200 data-[hover=true]:border-brand-200 data-[active=true]:bg-brand-300 data-[active=true]:border-brand-300 data-[focus-visible=true]:web:ring-indicator-info",
      muted:
        "bg-background-100 data-[active=true]:bg-background-200 data-[hover=true]:bg-background-50",
      lime: "bg-dopa-lime border-dopa-lime data-[hover=true]:bg-dopa-lime-deep data-[hover=true]:border-dopa-lime-deep data-[active=true]:bg-dopa-lime-deep data-[active=true]:border-dopa-lime-deep",
      sidebarDark:
        "bg-background-sidebar-dark border border-background-sidebar-dark data-[hover=true]:bg-background-sidebar-dark/90 data-[hover=true]:border-background-sidebar-dark/90 data-[active=true]:bg-background-sidebar-dark/80 data-[active=true]:border-background-sidebar-dark/80 data-[focus-visible=true]:web:ring-indicator-info",
    },
    variant: {
      link: "px-0",
      outline:
        "bg-transparent border data-[hover=true]:bg-background-50 data-[active=true]:bg-transparent",
      solid: "",
      rounded: "rounded-full",
    },
    rounded: {
      full: "rounded-full",
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      xl: "rounded-xl",
      "2xl": "rounded-2xl",
      "3xl": "rounded-3xl",
    },

    size: {
      "2xs": "px-3 h-7",
      xs: "px-3.5 h-8",
      sm: "px-4 h-9",
      md: "px-5 h-10",
      lg: "px-6 h-11",
      xl: "px-7 h-12",
      none: "",
      "circle-sm": "p-2 h-9 w-9",
      "circle-md": "p-2 h-10 w-10",
      "circle-lg": "p-2 h-12 w-12",
    },
    hidden: {
      true: "hidden",
      false: "",
    },
  },
  compoundVariants: [
    {
      action: "primary",
      variant: "link",
      class:
        "px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent",
    },
    {
      action: "secondary",
      variant: "link",
      class:
        "px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent",
    },
    {
      action: "positive",
      variant: "link",
      class:
        "px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent",
    },
    {
      action: "negative",
      variant: "link",
      class:
        "px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent",
    },
    {
      action: "muted",
      variant: "link",
      class:
        "px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent",
    },
    {
      action: "brand",
      variant: "link",
      class:
        "px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent",
    },
    {
      action: "primary",
      variant: "outline",
      class:
        "bg-transparent border border-primary-300 data-[hover=true]:bg-background-100 data-[hover=true]:border-primary-400 data-[active=true]:bg-background-200 data-[active=true]:border-primary-500",
    },
    {
      action: "secondary",
      variant: "outline",
      class:
        "bg-transparent data-[hover=true]:bg-background-50 data-[active=true]:bg-transparent",
    },
    {
      action: "positive",
      variant: "outline",
      class:
        "bg-transparent data-[hover=true]:bg-background-50 data-[active=true]:bg-transparent",
    },
    {
      action: "negative",
      variant: "outline",
      class:
        "bg-transparent data-[hover=true]:bg-background-50 data-[active=true]:bg-transparent",
    },
    {
      action: "brand",
      variant: "outline",
      class:
        "bg-transparent border border-brand-400 data-[hover=true]:bg-brand-50 data-[hover=true]:border-brand-500 data-[active=true]:bg-brand-100 data-[active=true]:border-brand-600 data-[disabled=true]:bg-transparent data-[disabled=true]:border-outline-200 data-[disabled=true]:opacity-100",
    },
    {
      action: "brand",
      variant: "solid",
      class:
        "data-[disabled=true]:bg-background-200 data-[disabled=true]:opacity-100",
    },
    {
      action: "brand",
      variant: "rounded",
      class:
        "data-[disabled=true]:bg-background-200 data-[disabled=true]:opacity-100",
    },
    {
      action: "negative",
      variant: "solid",
      class:
        "data-[disabled=true]:bg-background-300 data-[disabled=true]:opacity-100",
    },
    {
      action: "brandLight",
      variant: "solid",
      class:
        "data-[disabled=true]:bg-background-100 data-[disabled=true]:border-background-100 data-[disabled=true]:opacity-100",
    },
    {
      action: "brandLight",
      variant: "rounded",
      class:
        "data-[disabled=true]:bg-background-100 data-[disabled=true]:border-background-100 data-[disabled=true]:opacity-100",
    },
    {
      action: "brandLight",
      variant: "outline",
      class:
        "bg-transparent border border-brand-200 data-[hover=true]:bg-brand-50 data-[hover=true]:border-brand-300 data-[active=true]:bg-brand-100 data-[active=true]:border-brand-400 data-[disabled=true]:bg-transparent data-[disabled=true]:border-outline-200 data-[disabled=true]:opacity-100",
    },
    {
      action: "brandLight",
      variant: "link",
      class:
        "px-0 bg-transparent border-0 data-[hover=true]:bg-transparent data-[active=true]:bg-transparent",
    },
    {
      action: "muted",
      variant: "outline",
      class:
        "bg-transparent data-[hover=true]:bg-background-100 data-[active=true]:bg-transparent border-outline-200",
    },
    {
      action: "lime",
      variant: "outline",
      class:
        "bg-transparent border-dopa-lime data-[hover=true]:bg-dopa-lime/10 data-[active=true]:bg-dopa-lime/20",
    },
    {
      action: "lime",
      variant: "link",
      class:
        "px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent",
    },
    {
      action: "lime",
      variant: "solid",
      class:
        "data-[disabled=true]:bg-background-300 data-[disabled=true]:border-background-300 data-[disabled=true]:opacity-100",
    },
    {
      action: "sidebarDark",
      variant: "solid",
      class:
        "data-[disabled=true]:bg-background-sidebar-dark data-[disabled=true]:border-background-sidebar-dark data-[disabled=true]:opacity-40",
    },
    {
      action: "sidebarDark",
      variant: "rounded",
      class:
        "data-[disabled=true]:bg-background-sidebar-dark data-[disabled=true]:border-background-sidebar-dark data-[disabled=true]:opacity-40",
    },
    {
      action: "sidebarDark",
      variant: "outline",
      class:
        "bg-transparent border border-background-sidebar-dark data-[hover=true]:bg-background-sidebar-dark/10 data-[hover=true]:border-background-sidebar-dark data-[active=true]:bg-background-sidebar-dark/20 data-[active=true]:border-background-sidebar-dark data-[disabled=true]:bg-transparent data-[disabled=true]:border-outline-200 data-[disabled=true]:opacity-100",
    },
    {
      action: "sidebarDark",
      variant: "link",
      class:
        "px-0 bg-transparent border-0 data-[hover=true]:bg-transparent data-[active=true]:bg-transparent",
    },
  ],
});

const buttonTextStyle = tva({
  base: "text-typography-0 font-normal web:select-none font-body",
  parentVariants: {
    action: {
      primary:
        "text-primary-600 data-[hover=true]:text-primary-600 data-[active=true]:text-primary-700",
      secondary:
        "text-typography-500 data-[hover=true]:text-typography-600 data-[active=true]:text-typography-700",
      positive:
        "text-success-600 data-[hover=true]:text-success-600 data-[active=true]:text-success-700",
      negative:
        "text-error-600 data-[hover=true]:text-error-600 data-[active=true]:text-error-700",
      elevated: "text-typography-800",
      facebook: "text-typography-0",
      instagram: "text-typography-0 dark:text-typography-950",
      brand: "text-typography-brandButtonText",
      brandLight:
        "text-brand-600 data-[hover=true]:text-brand-700 data-[active=true]:text-brand-800",
      muted: "text-typography-700",
      default: "text-typography-700",
      lime: "text-typography-950",
      sidebarDark: "text-typography-0",
    },
    variant: {
      link: "data-[hover=true]:underline data-[active=true]:underline",
      outline: "",
      solid:
        "text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0",
    },
    size: {
      "2xs": "text-2xs",
      xs: "text-xs",
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
      xl: "text-xl",
    },
    isTruncated: {
      true: "web:truncate",
    },
  },
  parentCompoundVariants: [
    {
      variant: "solid",
      action: "primary",
      class:
        "text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0",
    },
    {
      variant: "solid",
      action: "secondary",
      class:
        "text-typography-800 data-[hover=true]:text-typography-800 data-[active=true]:text-typography-800",
    },
    {
      variant: "solid",
      action: "positive",
      class:
        "text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0",
    },
    {
      variant: "solid",
      action: "negative",
      class:
        "text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0",
    },
    {
      variant: "solid",
      action: "default",
      class:
        "text-typography-700 data-[hover=true]:text-typography-700 data-[active=true]:text-typography-700",
    },
    {
      variant: "solid",
      action: "elevated",
      class:
        "text-typography-800 data-[hover=true]:text-typography-800 data-[active=true]:text-typography-800",
    },
    {
      variant: "outline",
      action: "primary",
      class:
        "text-primary-500 data-[hover=true]:text-primary-500 data-[active=true]:text-primary-500",
    },
    {
      variant: "outline",
      action: "secondary",
      class:
        "text-typography-500 data-[hover=true]:text-primary-600 data-[active=true]:text-typography-700",
    },
    {
      variant: "outline",
      action: "positive",
      class:
        "text-primary-500 data-[hover=true]:text-primary-500 data-[active=true]:text-primary-500",
    },
    {
      variant: "outline",
      action: "negative",
      class:
        "text-primary-500 data-[hover=true]:text-primary-500 data-[active=true]:text-primary-500",
    },
    {
      variant: "solid",
      action: "muted",
      class:
        "text-typography-700 data-[hover=true]:text-typography-700 data-[active=true]:text-typography-700",
    },
    {
      variant: "solid",
      action: "brand",
      class:
        "text-typography-brandButtonText data-[hover=true]:text-typography-brandButtonText data-[active=true]:text-typography-brandButtonText data-[disabled=true]:text-typography-400",
    },
    {
      variant: "rounded",
      action: "brand",
      class:
        "text-typography-brandButtonText data-[hover=true]:text-typography-brandButtonText data-[active=true]:text-typography-brandButtonText data-[disabled=true]:text-typography-400",
    },
    {
      variant: "solid",
      action: "negative",
      class: "data-[disabled=true]:text-typography-500",
    },
    {
      variant: "solid",
      action: "brandLight",
      class:
        "text-brand-600 data-[hover=true]:text-brand-700 data-[active=true]:text-brand-800 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "rounded",
      action: "brandLight",
      class:
        "text-brand-600 data-[hover=true]:text-brand-700 data-[active=true]:text-brand-800 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "outline",
      action: "brandLight",
      class:
        "text-brand-600 data-[hover=true]:text-brand-700 data-[active=true]:text-brand-800 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "link",
      action: "brandLight",
      class:
        "text-brand-600 data-[hover=true]:text-brand-700 data-[active=true]:text-brand-800 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "outline",
      action: "brand",
      class:
        "text-brand-400 data-[hover=true]:text-brand-500 data-[active=true]:text-brand-600 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "link",
      action: "brand",
      class:
        "text-brand-400 data-[hover=true]:text-brand-500 data-[active=true]:text-brand-600 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "link",
      action: "muted",
      class: "text-typography-500 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "solid",
      action: "lime",
      class:
        "text-typography-950 font-bold data-[hover=true]:text-typography-950 data-[active=true]:text-typography-950 data-[disabled=true]:text-typography-500",
    },
    {
      variant: "outline",
      action: "lime",
      class:
        "text-dopa-lime-deep font-bold data-[hover=true]:text-dopa-lime-deep data-[active=true]:text-dopa-lime-deep data-[disabled=true]:text-typography-500",
    },
    {
      variant: "link",
      action: "lime",
      class:
        "text-dopa-lime-deep font-bold data-[disabled=true]:text-typography-500",
    },
    {
      variant: "solid",
      action: "sidebarDark",
      class:
        "text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0 data-[disabled=true]:text-typography-0",
    },
    {
      variant: "rounded",
      action: "sidebarDark",
      class:
        "text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0 data-[disabled=true]:text-typography-0",
    },
    {
      variant: "outline",
      action: "sidebarDark",
      class:
        "text-typography-900 data-[hover=true]:text-typography-900 data-[active=true]:text-typography-900 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "link",
      action: "sidebarDark",
      class:
        "text-typography-900 data-[hover=true]:text-typography-900 data-[active=true]:text-typography-900 data-[disabled=true]:text-typography-400",
    },
  ],
});

const buttonIconStyle = tva({
  base: "fill-none",
  variants: {
    hidden: {
      true: "hidden",
      false: "",
    },
  },
  parentVariants: {
    variant: {
      link: "data-[hover=true]:underline data-[active=true]:underline",
      outline: "",
      solid:
        "text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0",
    },
    size: {
      "2xs": "h-3 w-3",
      xs: "h-3.5 w-3.5",
      sm: "h-4 w-4",
      md: "h-[18px] w-[18px]",
      lg: "h-[18px] w-[18px]",
      xl: "h-5 w-5",
      "circle-sm": "h-5 w-5",
      "circle-md": "h-6 w-6",
      "circle-lg": "h-7 w-7",
    },
    action: {
      primary:
        "text-primary-600 data-[hover=true]:text-primary-600 data-[active=true]:text-primary-700",
      secondary:
        "text-typography-500 data-[hover=true]:text-typography-600 data-[active=true]:text-typography-700",
      positive:
        "text-success-600 data-[hover=true]:text-success-600 data-[active=true]:text-success-700",

      negative:
        "text-error-600 data-[hover=true]:text-error-600 data-[active=true]:text-error-700",
      card: "text-typography-700",
      elevated: "text-typography-700",
      brand: "text-typography-brandButtonText",
      brandLight:
        "text-brand-600 data-[hover=true]:text-brand-700 data-[active=true]:text-brand-800",
      muted: "text-typography-700",
      default: "text-typography-700",
      lime: "text-typography-950",
      sidebarDark: "text-typography-0",
    },
  },
  parentCompoundVariants: [
    {
      variant: "solid",
      action: "primary",
      class:
        "text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0",
    },
    {
      variant: "solid",
      action: "sidebarDark",
      class:
        "text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0",
    },
    {
      variant: "rounded",
      action: "sidebarDark",
      class:
        "text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0",
    },
    {
      variant: "outline",
      action: "sidebarDark",
      class:
        "text-typography-900 data-[hover=true]:text-typography-900 data-[active=true]:text-typography-900",
    },
    {
      variant: "link",
      action: "sidebarDark",
      class:
        "text-typography-900 data-[hover=true]:text-typography-900 data-[active=true]:text-typography-900",
    },
    {
      variant: "solid",
      action: "secondary",
      class:
        "text-typography-800 data-[hover=true]:text-typography-800 data-[active=true]:text-typography-800",
    },
    {
      variant: "solid",
      action: "positive",
      class:
        "text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0",
    },
    {
      variant: "solid",
      action: "negative",
      class:
        "text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0",
    },
    {
      variant: "outline",
      action: "brand",
      class:
        "text-brand-400 data-[hover=true]:text-brand-500 data-[active=true]:text-brand-600 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "solid",
      action: "brand",
      class:
        "text-typography-brandButtonText data-[hover=true]:text-typography-brandButtonText data-[active=true]:text-typography-brandButtonText data-[disabled=true]:text-typography-400",
    },
    {
      variant: "rounded",
      action: "brand",
      class:
        "text-typography-brandButtonText data-[hover=true]:text-typography-brandButtonText data-[active=true]:text-typography-brandButtonText data-[disabled=true]:text-typography-400",
    },
    {
      variant: "solid",
      action: "negative",
      class: "data-[disabled=true]:text-typography-500",
    },
    {
      variant: "solid",
      action: "brandLight",
      class:
        "text-brand-600 data-[hover=true]:text-brand-700 data-[active=true]:text-brand-800 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "rounded",
      action: "brandLight",
      class:
        "text-brand-600 data-[hover=true]:text-brand-700 data-[active=true]:text-brand-800 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "outline",
      action: "brandLight",
      class:
        "text-brand-600 data-[hover=true]:text-brand-700 data-[active=true]:text-brand-800 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "link",
      action: "brandLight",
      class:
        "text-brand-600 data-[hover=true]:text-brand-700 data-[active=true]:text-brand-800 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "link",
      action: "brand",
      class:
        "text-brand-400 data-[hover=true]:text-brand-500 data-[active=true]:text-brand-600 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "link",
      action: "muted",
      class: "text-typography-500 data-[disabled=true]:text-typography-400",
    },
    {
      variant: "solid",
      action: "lime",
      class:
        "text-typography-950 data-[hover=true]:text-typography-950 data-[active=true]:text-typography-950 data-[disabled=true]:text-typography-500",
    },
    {
      variant: "outline",
      action: "lime",
      class:
        "text-dopa-lime-deep data-[hover=true]:text-dopa-lime-deep data-[active=true]:text-dopa-lime-deep data-[disabled=true]:text-typography-500",
    },
    {
      variant: "link",
      action: "lime",
      class:
        "text-dopa-lime-deep data-[hover=true]:text-dopa-lime-deep data-[active=true]:text-dopa-lime-deep data-[disabled=true]:text-typography-500",
    },
  ],
});

const buttonGroupStyle = tva({
  base: "",
  variants: {
    space: {
      xs: "gap-1",
      sm: "gap-2",
      md: "gap-3",
      lg: "gap-4",
      xl: "gap-5",
      "2xl": "gap-6",
      "3xl": "gap-7",
      "4xl": "gap-8",
    },
    isAttached: {
      true: "gap-0",
    },
    flexDirection: {
      row: "flex-row",
      column: "flex-col",
      "row-reverse": "flex-row-reverse",
      "column-reverse": "flex-col-reverse",
    },
  },
});

type IButtonProps = Omit<
  React.ComponentPropsWithoutRef<typeof UIButton>,
  "context"
> &
  VariantProps<typeof buttonStyle> & { className?: string };

const Button = React.forwardRef<
  React.ElementRef<typeof UIButton>,
  IButtonProps
>(
  (
    {
      className,
      variant = "solid",
      size = "md",
      action = "primary",
      rounded,
      hidden,
      ...props
    },
    ref,
  ) => {
    return (
      <UIButton
        ref={ref}
        {...props}
        className={buttonStyle({
          variant,
          size,
          action,
          rounded,
          hidden,
          class: className,
        })}
        context={{ variant, size, action }}
      />
    );
  },
);

type IButtonTextProps = React.ComponentPropsWithoutRef<typeof UIButton.Text> &
  VariantProps<typeof buttonTextStyle> & { className?: string };

const ButtonText = React.forwardRef<
  React.ElementRef<typeof UIButton.Text>,
  IButtonTextProps
>(({ className, variant, size, action, ...props }, ref) => {
  const {
    variant: parentVariant,
    size: parentSize,
    action: parentAction,
  } = useStyleContext(SCOPE);

  return (
    <UIButton.Text
      ref={ref}
      {...props}
      className={buttonTextStyle({
        parentVariants: {
          variant: parentVariant,
          size: parentSize,
          action: parentAction,
        },
        variant,
        size,
        action,
        class: className,
      })}
    />
  );
});

const ButtonSpinner = UIButton.Spinner;

type IButtonIcon = React.ComponentPropsWithoutRef<typeof UIButton.Icon> &
  VariantProps<typeof buttonIconStyle> & {
    className?: string | undefined;
    as?: React.ElementType;
    height?: number;
    width?: number;
  };

const ButtonIcon = React.forwardRef<
  React.ComponentRef<typeof UIButton.Icon>,
  IButtonIcon
>(({ className, size, ...props }, ref) => {
  const {
    variant: parentVariant,
    size: parentSize,
    action: parentAction,
  } = useStyleContext(SCOPE);

  if (typeof size === "number") {
    return (
      <UIButton.Icon
        ref={ref}
        {...props}
        className={buttonIconStyle({
          parentVariants: {
            variant: parentVariant,
            action: parentAction,
          },
          class: className,
        })}
        size={size}
      />
    );
  } else if (
    (props.height !== undefined || props.width !== undefined) &&
    size === undefined
  ) {
    return (
      <UIButton.Icon
        ref={ref}
        {...props}
        className={buttonIconStyle({
          parentVariants: {
            variant: parentVariant,
            action: parentAction,
          },
          class: className,
        })}
      />
    );
  }

  const sizeDict = {
    "2xs": 14,
    xs: 16,
    sm: 18,
    md: 20,
    lg: 20,
    xl: 24,
    "2xl": 28,
    "circle-sm": 24,
    "circle-md": 28,
    "circle-lg": 30,
  };
  return (
    <UIButton.Icon
      {...props}
      className={buttonIconStyle({
        parentVariants: {
          variant: parentVariant,
          action: parentAction,
        },
        class: className,
      })}
      ref={ref}
      size={sizeDict[(size ?? parentSize) as keyof typeof sizeDict]}
    />
  );
});

type IButtonGroupProps = React.ComponentPropsWithoutRef<typeof UIButton.Group> &
  VariantProps<typeof buttonGroupStyle>;

const ButtonGroup = React.forwardRef<
  React.ElementRef<typeof UIButton.Group>,
  IButtonGroupProps
>(
  (
    {
      className,
      space = "md",
      isAttached = false,
      flexDirection = "column",
      ...props
    },
    ref,
  ) => {
    return (
      <UIButton.Group
        className={buttonGroupStyle({
          class: className,
          space,
          isAttached,
          flexDirection,
        })}
        {...props}
        ref={ref}
      />
    );
  },
);

Button.displayName = "Button";
ButtonText.displayName = "ButtonText";
ButtonSpinner.displayName = "ButtonSpinner";
ButtonIcon.displayName = "ButtonIcon";
ButtonGroup.displayName = "ButtonGroup";

export { Button, ButtonGroup, ButtonIcon, ButtonSpinner, ButtonText };
